import * as RadioGroup from "@radix-ui/react-radio-group";
import { useId, type ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

// =============================================================================
// OptionTiles
//
// A radio group rendered as selectable tiles — icon, label and a one-line
// description — for the questions where the choice benefits from a word of
// explanation (trip type, level of service). Built on the same Radix radio
// group as ChipRadioGroup, so keyboard behaviour (arrow keys, roving focus)
// and semantics are identical.
//
// The description is linked with aria-describedby and hidden from the
// accessible NAME, so the option is still announced — and found by tests —
// as just its label ("Round Trip"), with the description read after it.
// =============================================================================

export interface OptionTile<T extends string = string> {
  value: T;
  label: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface OptionTilesProps<T extends string = string> {
  value: T;
  onValueChange: (value: T) => void;
  options: OptionTile<T>[];
  ariaLabel: string;
  invalid?: boolean;
  /** Grid column classes, e.g. "sm:grid-cols-3". */
  className?: string;
}

export function OptionTiles<T extends string = string>({
  value,
  onValueChange,
  options,
  ariaLabel,
  invalid,
  className,
}: OptionTilesProps<T>) {
  const baseId = useId();
  return (
    <RadioGroup.Root
      value={value}
      onValueChange={(v) => onValueChange(v as T)}
      aria-label={ariaLabel}
      className={cn("grid gap-3", className)}
    >
      {options.map((opt, i) => {
        const descriptionId = opt.description ? `${baseId}-desc-${i}` : undefined;
        return (
          <RadioGroup.Item
            key={opt.value}
            value={opt.value}
            disabled={opt.disabled}
            aria-describedby={descriptionId}
            className={cn(
              "group relative flex min-h-[44px] items-start gap-3 rounded-xl border border-border bg-foreground/[0.02] p-3.5 text-left transition",
              "hover:border-foreground/25 hover:bg-foreground/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "data-[state=checked]:border-primary/70 data-[state=checked]:bg-primary/[0.08]",
              "data-[state=checked]:shadow-[0_0_0_1px_hsl(var(--primary)/0.45),0_12px_30px_-18px_hsl(var(--primary)/0.8)]",
              "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40",
              invalid && "border-destructive shadow-[0_0_0_3px_hsl(var(--destructive)/0.18)]",
            )}
          >
            {opt.icon ? (
              <span
                aria-hidden="true"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-foreground/[0.05] text-foreground/80 transition group-data-[state=checked]:bg-primary/15 group-data-[state=checked]:text-primary"
              >
                {opt.icon}
              </span>
            ) : null}
            <span className="flex min-w-0 flex-col gap-0.5 pr-6 pt-0.5">
              <span className="text-sm font-semibold leading-tight text-foreground">
                {opt.label}
              </span>
              {opt.description ? (
                <span
                  id={descriptionId}
                  aria-hidden="true"
                  className="text-xs leading-snug text-muted-foreground"
                >
                  {opt.description}
                </span>
              ) : null}
            </span>
            <RadioGroup.Indicator className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
            </RadioGroup.Indicator>
          </RadioGroup.Item>
        );
      })}
    </RadioGroup.Root>
  );
}
